import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { amiGet, amiPost, Card, Kpi, Loading, NotYet, num, pct, tsFmt, isoFmt, errMsg } from "@/lib/ami";

const LABEL = { baseline_prior: "Baseline: Grundrate", baseline_momentum: "Baseline: Momentum", logreg: "Logistische Regression", gbm: "Gradient Boosting" };

function Current() {
  const q = useQuery({ queryKey: ["ami-pred-current"], queryFn: () => amiGet("/predictions/current"), refetchInterval: 60000 });
  if (q.isLoading) return <Loading />;
  const { model, predictions } = q.data;
  return (
    <Card title="Aktuelle Prognosen" testid="ami-pred-current">
      <div className="mb-4 text-sm" data-testid="ami-model-status">Modellstatus: <span className={model.status === "validiert" ? "text-emerald-400" : "text-amber-300"}>{model.status}</span>{model.version ? ` · ${model.version}` : ""}</div>
      {predictions.length === 0 ? <NotYet text="Noch keine Prognose. Trainiere zuerst ein Modell – Prognosen entstehen danach jede volle Stunde." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {predictions.map((p) => (
            <div key={p.horizon_h} className="rounded-lg border border-border p-4" data-testid={`ami-pred-${p.horizon_h}`}>
              <div className="text-xs text-muted-foreground">{p.horizon_h} h · {LABEL[p.model]} · {p.validated ? "validiert" : "NICHT validiert – nur zur Beobachtung"}</div>
              <div className={`mt-2 font-mono text-2xl ${p.p_up >= 0.5 ? "text-emerald-400" : "text-rose-400"}`}>{num(p.p_up * 100, 1)} % steigend</div>
              <div className="text-xs text-muted-foreground mt-1">80-%-Spanne {num(p.range_lo, 0)} – {num(p.range_hi, 0)} $ · Basis {num(p.ref_px, 0)} $ · fällig {tsFmt(p.due_ts)}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function RunDetail({ run }) {
  const best = run.summary;
  return (
    <div className="mt-3 overflow-x-auto" data-testid={`ami-run-detail-${run.horizon_h}`}>
      <table className="w-full text-xs font-mono">
        <thead><tr className="text-left uppercase tracking-wider text-muted-foreground font-sans"><th className="py-2">Modell (out-of-sample)</th><th>n</th><th>Trefferquote</th><th>Brier</th><th>AUC</th><th>Skill vs. Baseline</th><th>positive Folds</th><th>Status</th></tr></thead>
        <tbody>{Object.entries(best).map(([m, s]) => {
          const v = run.verdict[m];
          return (
            <tr key={m} className="border-t border-border/60"><td className="py-1.5 font-sans">{LABEL[m]}</td><td>{num(s.n, 0)}</td><td>{num(s.accuracy * 100, 1)} %</td><td>{num(s.brier, 4)}</td><td>{s.auc == null ? "–" : num(s.auc, 3)}</td>
              <td>{v ? pct(v.brier_skill_vs_best_baseline * 100, 2) : "–"}</td><td>{v ? `${num(v.positive_fold_share * 100, 0)} %` : "–"}</td><td className="font-sans">{v ? (v.validated ? "validiert" : "nicht validiert") : "Vergleich"}</td></tr>
          );
        })}</tbody>
      </table>
      <p className="mt-2 text-xs text-muted-foreground">Walk-Forward: Training ≥ 2 Jahre, Test je 90 Tage, Embargo = Horizont. Validiert nur bei Brier-Skill &gt; 0,2 % gegenüber der besten Baseline UND in ≥ 60 % der Folds besser. Abdeckung der 80-%-Spanne: {num(run.range_coverage_pct, 1)} %. Daten {tsFmt(run.data_from)} – {tsFmt(run.data_to)}.</p>
    </div>
  );
}

function Runs() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["ami-runs"], queryFn: () => amiGet("/models/runs"), refetchInterval: 5000 });
  const train = useMutation({ mutationFn: (h) => amiPost("/models/train", { horizon_h: h }), onSuccess: () => { toast.success("Training gestartet"); qc.invalidateQueries({ queryKey: ["ami-runs"] }); }, onError: (e) => toast.error(errMsg(e)) });
  const [open, setOpen] = useState(null);
  const detail = useQuery({ queryKey: ["ami-run", open], queryFn: () => amiGet(`/models/runs/${open}`), enabled: !!open });
  return (
    <Card title="Modelle & Validierung" testid="ami-runs" actions={<div className="flex gap-2">{[4, 24].map((h) => <Button key={h} size="sm" onClick={() => train.mutate(h)} disabled={train.isPending} data-testid={`ami-train-${h}`}>{h} h trainieren</Button>)}</div>}>
      {q.isLoading ? <Loading /> : q.data.length === 0 ? <NotYet text="Noch kein Training. Benötigt die 1h-Historie von BTC-USDT-SWAP (Historische Marktanalyse)." /> : q.data.map((r) => (
        <div key={r.id} className="py-3 border-b border-border/60 last:border-0" data-testid={`ami-run-${r.id}`}>
          <button className="w-full flex flex-wrap justify-between gap-2 text-sm text-left" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`ami-run-toggle-${r.id}`}>
            <span className="font-mono">{r.horizon_h} h · {r.version || "läuft…"}</span>
            <span className="text-xs text-muted-foreground">{r.status === "running" ? "Training läuft…" : r.status === "error" ? `Fehler: ${r.error}` : `${r.validated ? "validiert" : "nicht validiert"} · bestes ML: ${LABEL[r.best_model]} · ${isoFmt(r.finished_at)}`}</span>
          </button>
          {open === r.id && detail.data && detail.data.status === "done" && <RunDetail run={detail.data} />}
        </div>
      ))}
    </Card>
  );
}

function History() {
  const [h, setH] = useState(24);
  const q = useQuery({ queryKey: ["ami-pred-hist", h], queryFn: () => amiGet("/predictions/history", { horizon_h: h, limit: 100 }), refetchInterval: 60000 });
  return (
    <Card title="Prognosehistorie (live)" testid="ami-pred-history" actions={<div className="flex gap-1">{[4, 24].map((x) => <button key={x} onClick={() => setH(x)} className={`px-3 py-1 rounded-full text-xs border border-border ${h === x ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid={`ami-pred-hist-${x}`}>{x} h</button>)}</div>}>
      {q.isLoading ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {Object.entries(q.data.live_stats).map(([m, s]) => <Kpi key={m} label={LABEL[m]} value={`${num(s.hit_rate_pct, 1)} %`} hint={`${s.n} ausgewertet · Brier ${num(s.brier, 4)} · Spanne ${num(s.range_coverage_pct, 0)} %`} />)}
          </div>
          {q.data.rows.length === 0 ? <NotYet text="Noch keine Live-Prognosen gespeichert." /> : (
            <div className="overflow-x-auto max-h-80"><table className="w-full text-xs font-mono">
              <thead><tr className="text-left uppercase tracking-wider text-muted-foreground font-sans"><th className="py-2">Erstellt</th><th>P(steigt)</th><th>Basis</th><th>Ergebnis</th><th>Treffer</th></tr></thead>
              <tbody>{q.data.rows.map((p) => <tr key={p.id} className="border-t border-border/60"><td className="py-1.5">{tsFmt(p.made_at)}</td><td>{num(p.p_up * 100, 1)} %</td><td>{num(p.ref_px, 0)}</td><td>{p.outcome ? pct(p.outcome.ret_pct) : `offen bis ${tsFmt(p.due_ts)}`}</td><td>{p.outcome ? (p.outcome.hit ? "ja" : "nein") : "–"}</td></tr>)}</tbody>
            </table></div>
          )}
        </>
      )}
    </Card>
  );
}

export default function AmiPredictions() {
  return <div className="space-y-6" data-testid="ami-predictions"><Current /><Runs /><History /></div>;
}
